import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Award } from "lucide-react";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import { Button } from "@/components/ui/button";

/**
 * Đánh giá hiệu quả công việc — dùng chung cho "Tự đánh giá HQCV"
 * (taskRatingPersonal.zul) và "Quản lý đánh giá HQCV" (taskRating.zul) của hệ cũ.
 * Cùng lưới "Tổng hợp đánh giá" 9 cột; bản quản lý có thêm 3 ô chọn nhân viên
 * theo trạng thái và lưới "Đánh giá công việc" bên dưới.
 */
type Employee = {
  EMPLOYEE_ID: number;
  FULL_NAME: string | null;
  EMPLOYEE_CODE: string | null;
  STATUS: number | null;
};

type SummaryRow = {
  EMP_RATING_ID: number;
  EMPLOYEE_ID: number;
  FULL_NAME: string | null;
  STATUS: number | null;
  TOTAL_TASKS: number;
  RATED_TASKS: number;
  AVR_POINT_STAFF: number | null;
  AVR_POINT: number | null;
  KI_NAME_STAFF: string | null;
  KI_NAME: string | null;
};

type TaskRow = {
  TASK_ID: number;
  TASK_NAME: string | null;
  CONTENT: string | null;
  START_TIME: string | null;
  END_TIME: string | null;
  TARGET: string | null;
  TASK_RESULT: string | null;
  WEIGHT: number | null;
  RATING_QUALITY_YOURSELF: number | null;
  RATING_POINT: number | null;
};

const STATUS_LABEL: Record<number, string> = { 1: "Chờ đánh giá", 2: "Đã đánh giá" };
const day = (v: string | null) => (v ? v.slice(0, 10) : "—");
const num = (v: number | null) => (v == null ? "—" : String(v));

export function HqcvPage({ mode }: { mode: "self" | "manage" }) {
  const isManage = mode === "manage";
  const [period, setPeriod] = useState("");
  const [applied, setApplied] = useState("");
  const [employeeId, setEmployeeId] = useState<number | "">("");

  const { data: emps } = useQuery({
    queryKey: ["hqcv-employees", applied],
    queryFn: async () => {
      const { data } = await api.get("/task/rating/employees", {
        params: { period: applied || undefined },
      });
      return (data.data ?? data) as {
        period: string;
        items: Employee[];
        assessed: Employee[];
        waiting: Employee[];
        notSent: Employee[];
      };
    },
  });

  const { data: summary, isLoading } = useQuery({
    queryKey: ["hqcv-summary", applied, employeeId],
    queryFn: async () => {
      const { data } = await api.get("/task/rating/summary", {
        params: { period: applied || undefined, employeeId: employeeId || undefined },
      });
      return (data.data ?? data) as { period: string; items: SummaryRow[] };
    },
  });

  const { data: tasks } = useQuery({
    enabled: isManage && employeeId !== "",
    queryKey: ["hqcv-tasks", applied, employeeId],
    queryFn: async () => {
      const { data } = await api.get("/task/rating/tasks", {
        params: { employeeId, period: applied || undefined },
      });
      return (data.data ?? data) as { period: string; items: TaskRow[] };
    },
  });

  const summaryColumns: Column<SummaryRow>[] = [
    {
      key: "name",
      header: "Người thực hiện",
      render: (r) => <span className="font-medium">{r.FULL_NAME ?? "—"}</span>,
    },
    {
      key: "status",
      header: "Trạng thái",
      width: "130px",
      render: (r) => (r.STATUS == null ? "Chưa gửi" : (STATUS_LABEL[r.STATUS] ?? `#${r.STATUS}`)),
    },
    {
      key: "total",
      header: "Tổng số công việc",
      width: "140px",
      align: "right",
      render: (r) => <span className="tabular-nums">{r.TOTAL_TASKS}</span>,
    },
    {
      key: "rated",
      header: "Số việc đánh giá",
      width: "140px",
      align: "right",
      render: (r) => <span className="tabular-nums">{r.RATED_TASKS}</span>,
    },
    // Hệ cũ để tiêu đề hai tầng: "Tự đánh giá" / "QL đánh giá" mỗi bên phủ hai
    // cột con Điểm XH và Kết quả XH.
    {
      key: "selfPoint",
      group: "Tự đánh giá",
      header: "Điểm XH",
      width: "150px",
      align: "right",
      render: (r) => <span className="tabular-nums">{num(r.AVR_POINT_STAFF)}</span>,
    },
    {
      key: "selfRank",
      group: "Tự đánh giá",
      header: "Kết quả XH",
      width: "170px",
      render: (r) => r.KI_NAME_STAFF ?? "—",
    },
    {
      key: "mgrPoint",
      group: "QL đánh giá",
      header: "Điểm XH",
      width: "150px",
      align: "right",
      render: (r) => <span className="tabular-nums">{num(r.AVR_POINT)}</span>,
    },
    {
      key: "mgrRank",
      group: "QL đánh giá",
      header: "Kết quả XH",
      width: "170px",
      render: (r) => r.KI_NAME ?? "—",
    },
    {
      key: "actions",
      header: "Thao tác",
      width: "120px",
      align: "center",
      render: (r) => (
        <Button size="sm" variant="outline" onClick={() => setEmployeeId(r.EMPLOYEE_ID)}>
          Xem
        </Button>
      ),
    },
  ];

  const taskColumns: Column<TaskRow>[] = [
    {
      key: "stt",
      header: "STT",
      width: "56px",
      align: "center",
      render: (_r, i) => <span className="text-muted-foreground tabular-nums">{i + 1}</span>,
    },
    {
      key: "name",
      header: "Tên công việc",
      render: (r) => <span className="font-medium">{r.TASK_NAME ?? "—"}</span>,
    },
    {
      key: "content",
      header: "Nội dung công việc",
      width: "240px",
      render: (r) => r.CONTENT ?? "—",
    },
    {
      key: "start",
      header: "Ngày phát sinh",
      width: "120px",
      render: (r) => <span className="tabular-nums text-sm">{day(r.START_TIME)}</span>,
    },
    {
      key: "end",
      header: "Hạn hoàn thành",
      width: "130px",
      render: (r) => <span className="tabular-nums text-sm">{day(r.END_TIME)}</span>,
    },
    {
      key: "target",
      header: "Mục tiêu, kết quả mong muốn",
      width: "220px",
      render: (r) => r.TARGET ?? "—",
    },
    {
      key: "result",
      header: "Kết quả thực hiện",
      width: "200px",
      render: (r) => r.TASK_RESULT ?? "—",
    },
    {
      key: "weight",
      header: "Trọng số",
      width: "90px",
      align: "right",
      render: (r) => <span className="tabular-nums">{num(r.WEIGHT)}</span>,
    },
    {
      key: "selfPoint",
      header: "Điểm tự đánh giá",
      width: "140px",
      align: "right",
      render: (r) => <span className="tabular-nums">{num(r.RATING_QUALITY_YOURSELF)}</span>,
    },
    {
      key: "mgrPoint",
      header: "Điểm QL đánh giá",
      width: "140px",
      align: "right",
      render: (r) => <span className="tabular-nums">{num(r.RATING_POINT)}</span>,
    },
  ];

  const pick = (list: Employee[] | undefined, label: string) => ({
    label: `${label} (${list?.length ?? 0})`,
    control: (
      <select
        className={filterInputClass}
        value={employeeId}
        onChange={(e) => setEmployeeId(e.target.value ? Number(e.target.value) : "")}
      >
        <option value="">---- Chọn ----</option>
        {(list ?? []).map((e) => (
          <option key={e.EMPLOYEE_ID} value={e.EMPLOYEE_ID}>
            {e.FULL_NAME ?? e.EMPLOYEE_CODE ?? e.EMPLOYEE_ID}
          </option>
        ))}
      </select>
    ),
  });

  const periodField = {
    label: "Kỳ đánh giá",
    control: (
      <input
        className={filterInputClass}
        placeholder={emps?.period ?? "MM/yyyy"}
        value={period}
        onChange={(e) => setPeriod(e.target.value)}
      />
    ),
  };

  return (
    <div>
      <PageHeader
        title={isManage ? "Quản lý đánh giá HQCV" : "Tự đánh giá HQCV"}
        subtitle={`Kỳ ${emps?.period ?? "—"} · ${emps?.items.length ?? 0} nhân viên trong phạm vi`}
        breadcrumb={`Công việc › ${isManage ? "Quản lý đánh giá" : "Tự đánh giá"}`}
      />
      <SearchPanel
        fields={
          isManage
            ? [
                pick(emps?.assessed, "Nhân viên đã được ĐG"),
                pick(emps?.waiting, "Nhân viên chờ ĐG"),
                pick(emps?.notSent, "Nhân viên chưa gửi ĐG"),
                periodField,
              ]
            : [pick(emps?.items, "Nhân viên cần đánh giá"), periodField]
        }
        onSubmit={() => setApplied(period)}
      />

      <div className="text-xxs uppercase tracking-wider text-muted-foreground mb-2">
        Tổng hợp đánh giá
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[1400px]">
          <DataTable<SummaryRow>
            columns={summaryColumns}
            rows={summary?.items ?? []}
            keyBy={(r) => r.EMP_RATING_ID}
            isLoading={isLoading}
            empty={
              <>
                <Award className="inline h-4 w-4 mr-1" /> Chưa có bản đánh giá nào trong kỳ này — hệ
                cũ cũng đang trống (bảng <code>emp_rating</code> = 0 bản ghi).
              </>
            }
            dense
          />
        </div>
      </div>

      {isManage && (
        <>
          <div className="text-xxs uppercase tracking-wider text-muted-foreground mt-6 mb-2">
            Đánh giá công việc
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[1600px]">
              <DataTable<TaskRow>
                columns={taskColumns}
                rows={tasks?.items ?? []}
                keyBy={(r) => r.TASK_ID}
                empty={
                  employeeId === ""
                    ? "Chọn một nhân viên ở trên để xem công việc cần đánh giá."
                    : "Nhân viên này chưa có công việc nào trong kỳ."
                }
                dense
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
